// Which models the platform calls, and the request each one accepts.
//
// One copy for the app (src/ imports it) and the edge functions (ambient,
// slack-qa). Two places cannot import it and keep a mirror that says so:
// the Vercel route api/llm/chat/simple.js (plain JS) and apps/peak-llm (its
// own app).
//
// The current generation, read from the vendors' own pages on 2026-10-28:
// OpenAI's GPT-6 family (developers.openai.com/api/docs/models) and
// Anthropic's Claude 5. OpenAI removes GPT-4o, GPT-4o mini and GPT-4.1 from
// the API on 2027-01-14 and plain GPT-5 on 2026-12-11
// (developers.openai.com/api/docs/deprecations).
//
// What each accepts, tested the same day: every OpenAI model after GPT-4
// refuses max_tokens (it takes max_completion_tokens, which also counts its
// reasoning) and any temperature but the default; Claude 5 refuses any
// temperature but the default. GPT-4o takes both forms.

export type ModelProvider = 'openai' | 'anthropic';

export interface ModelChoice {
  id: string;
  name: string;
  provider: ModelProvider;
}

/** What every agent and assist uses unless a person chose otherwise (owner, 2026-10-28). */
export const DEFAULT_MODEL = 'gpt-6.1-sol';

/** What a call uses when it must go to Anthropic and names no current Claude model. */
export const DEFAULT_CLAUDE_MODEL = 'claude-sonnet-5-5';

/** The models a person can choose, the default first. */
export const CURRENT_MODELS: ModelChoice[] = [
  { id: 'gpt-6.1-sol', name: 'GPT-6.1 Sol', provider: 'openai' },
  { id: 'gpt-6-astra', name: 'GPT-6 Astra', provider: 'openai' },
  { id: 'gpt-6.1-terra', name: 'GPT-6.1 Terra', provider: 'openai' },
  { id: 'gpt-6-luna', name: 'GPT-6 Luna', provider: 'openai' },
  { id: 'claude-opus-5-5', name: 'Claude Opus 5.5', provider: 'anthropic' },
  { id: 'claude-sonnet-5-5', name: 'Claude Sonnet 5.5', provider: 'anthropic' },
  { id: 'claude-haiku-5-5', name: 'Claude Haiku 5.5', provider: 'anthropic' },
];

/** Reasoning a newer OpenAI model may spend before it answers, on top of the answer's own length. */
const REASONING_ALLOWANCE = 2000;

function norm(model: string | null | undefined): string {
  return (model ?? '').trim().toLowerCase();
}

export function providerOf(model: string): ModelProvider {
  return norm(model).startsWith('claude') ? 'anthropic' : 'openai';
}

/** GPT-3.5 and GPT-4 (4o, 4.1, their minis): the only OpenAI models that take max_tokens and a chosen temperature. */
export function isLegacyOpenAIModel(model: string): boolean {
  return /^gpt-(3\.5|4)/.test(norm(model));
}

/** Claude 3 and 4 take a chosen temperature; Claude 5 and later take only the default. */
export function claudeAcceptsTemperature(model: string): boolean {
  const m = norm(model);
  if (/^claude-3/.test(m)) return true;
  const gen = m.match(/^claude-(?:opus|sonnet|haiku)-(\d+)/);
  return gen !== null && Number(gen[1]) < 5;
}

/**
 * The model to call for one a setting names. A model its vendor has retired,
 * or has announced for removal, becomes the current one, so a stored setting
 * never makes a call fail: GPT-3.5, GPT-4 (4o, 4.1), plain GPT-5 and o1/o3
 * become the default; Claude 3 and 4 become Claude 5 of the same family.
 */
export function currentModelFor(model: string | null | undefined): string {
  const m = norm(model);
  if (!m) return DEFAULT_MODEL;
  if (m.startsWith('claude')) {
    if (/^claude-(opus|sonnet|haiku)-[5-9]/.test(m) || m.startsWith('claude-fable')) return model!.trim();
    if (m.includes('opus')) return 'claude-opus-5-5';
    if (m.includes('haiku')) return 'claude-haiku-5-5';
    return DEFAULT_CLAUDE_MODEL;
  }
  if (isLegacyOpenAIModel(m) || /^gpt-5(-|$)/.test(m) || /^o[13](-|$)/.test(m)) return DEFAULT_MODEL;
  return model!.trim();
}

/** The parts of an OpenAI chat/completions body that depend on the model. */
export function openAIModelParams(model: string, opts: { maxTokens?: number; temperature?: number }): Record<string, unknown> {
  const maxTokens = opts.maxTokens && opts.maxTokens > 0 ? opts.maxTokens : 2000;
  if (isLegacyOpenAIModel(model)) {
    return { max_tokens: maxTokens, ...(opts.temperature !== undefined ? { temperature: opts.temperature } : {}) };
  }
  return { max_completion_tokens: maxTokens + REASONING_ALLOWANCE, reasoning_effort: 'low' };
}

/** The parts of an Anthropic messages body that depend on the model. */
export function anthropicModelParams(model: string, opts: { maxTokens?: number; temperature?: number }): Record<string, unknown> {
  const maxTokens = opts.maxTokens && opts.maxTokens > 0 ? opts.maxTokens : 1024;
  return {
    max_tokens: maxTokens,
    ...(opts.temperature !== undefined && claudeAcceptsTemperature(model) ? { temperature: opts.temperature } : {}),
  };
}

export interface OpenAIChatCompletionInput {
  model: string;
  messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>;
  maxTokens?: number;
  temperature?: number;
}

/** A whole OpenAI chat/completions body, for the model a setting names. */
export function buildOpenAIChatCompletionBody(input: OpenAIChatCompletionInput): Record<string, unknown> {
  const model = currentModelFor(input.model);
  return { model, messages: input.messages, ...openAIModelParams(model, input) };
}
